// Typed agent BEHAVIOR config (WS-D of the configurability remediation plan,
// `personal_documentation/configurability-remediation-scope-2026-08-03.md`).
//
// The high-value AI-behavior knobs of a persistent/orchestration agent —
// long-term memory, reflection, the autonomous goal loop, host-duty/continuity,
// and code-mode — have historically ridden the untyped `Agent.config` blob
// (`z.record(z.string(), z.unknown())`). They were parsed by hand off raw keys
// in the persistent-agent host (`apps/persistent-agent-host/src/runtime.ts`
// `resolve{Memory,Goal,Continuity,CodeMode}` and `reflection.ts`
// `resolveReflectionConfig`), with no Zod schema, no DTO field, no Swagger
// example, and no validation — so a typo silently disabled the feature.
//
// This module is the single typed contract for those keys (repo rule #3: every
// cross-package contract is a Zod schema in `@cortex/types`). Two surfaces:
//
//   1. `AgentBehaviorConfigSchema` — the STRICT input contract. What the API's
//      `behaviorConfig` field accepts; a typo `400`s at the boundary instead of
//      silently no-op'ing. Its sub-object KEY NAMES MATCH the host's existing
//      `config.*` keys exactly, so the service can merge a validated
//      `behaviorConfig` straight into the stored `config` and the current host
//      resolvers read it with no host change (the host refactor to consume the
//      parser is a follow-up — WS-D commit 3).
//
//   2. `parseAgentBehaviorConfig(config)` — the TOLERANT reader. Mirrors the
//      host resolvers exactly, including the legacy shorthands (`reflection:
//      true`, `continuity: 'mission'|'always'|true`, the `hostDuty` alias,
//      `codeMode: true`), and normalizes them into the typed shape. Never
//      throws (back-compat: old rows and old callers keep working); junk keys
//      are ignored the same way the resolvers ignore them.
//
// Benchmark link: `AgentBehaviorConfigSchema` is the substrate the orchestration
// benchmark harness's `RunProfileSchema` (W3, §6.3 of
// `new_documentation_07_2026/design docs/orchestration-benchmark-harness-design.md`)
// diffs one variable over. `AgentMemoryConfigSchema.mode` (off/seeded/live) is
// the memory-ablation axis W4 needs (and Fin's live "agent-level memory disable"
// ask); the field is defined here now, host wiring of SEEDED/LIVE is W4.
import { z } from 'zod';
import { extractMemoryAnchors, MemoryAnchorSetSchema } from './memory-anchors.js';
// ─── Sub-schemas (STRICT input contract; keys match the host's config.* keys) ──
/**
 * Long-term-memory ablation axis (benchmark W4). Orthogonal to `modelConfigId`,
 * which powers observational/working memory:
 *   - `off`    — skip memory injection + tools entirely (clean control arm).
 *   - `seeded` — load a pinned fixture, freeze writes (fair, deterministic).
 *   - `live`   — real memory + reflection (the product default).
 * Defined now for the typed door; the host's SEEDED/LIVE wiring is W4.
 */
export const MemoryModeSchema = z.enum(['off', 'seeded', 'live']);
/**
 * `config.memory`. Observational/working memory is ALWAYS ON with the
 * deployment-wide default observer (gpt-5.6-luna) — there is no per-agent
 * switch and no per-agent model choice. `modelConfigId` is DEPRECATED and
 * ignored by the host (kept in the schema so stored configs and existing
 * callers keep validating; the console actively clears it on save). `mode`
 * is the ablation axis for the unified-store memory block + memory tools
 * only — it does NOT turn off Observational Memory. `maxLearnings` remains
 * live working-memory guidance.
 */
export const AgentMemoryConfigSchema = z
    .object({
    modelConfigId: z.string().min(1).optional(),
    maxLearnings: z.number().int().positive().max(500).optional(),
    mode: MemoryModeSchema.optional(),
})
    .strict();
/**
 * `config.reflection`. `enabled` opts a supervisor into the reflection
 * heartbeat + memory charter + salience nudge. `charter`/`prompt`/thresholds
 * override the host's hardcoded consts (WS-A wires them; writing them is inert
 * until then, but validated + persisted now).
 */
export const AgentReflectionConfigSchema = z
    .object({
    enabled: z.boolean().optional(),
    /** 5-part cron; absent ⇒ the host's `reflectionHeartbeatDefaultCron`. */
    cron: z.string().min(1).optional(),
    /** WS-A: overrides `reflectionMemoryCharter` ("what this fleet remembers"). */
    charter: z.string().max(8000).optional(),
    /** WS-A: overrides `reflectionHeartbeatPrompt`. */
    prompt: z.string().max(20_000).optional(),
    /** WS-A advanced: overrides `reflectionSalienceThreshold` (default 10). */
    salienceThreshold: z.number().int().positive().optional(),
    /** WS-A advanced: overrides `reflectionPeerToolErrorThreshold` (default 5). */
    peerToolErrorThreshold: z.number().int().positive().optional(),
    /**
     * Overrides the host's default principle-judge prompt
     * (`principleJudgePrompt` in the persistent-agent host) — the classifier
     * run over each memory this supervisor proposes, promoting qualifying
     * proposals to type PRINCIPLE.
     */
    judgePrompt: z.string().max(20_000).optional(),
    /**
     * ModelConfig id the principle judge runs on. Absent ⇒ the supervisor's
     * own resolved model (same fallback as `goal.judgeModelConfigId`).
     */
    judgeModelConfigId: z.string().min(1).optional(),
})
    .strict();
/**
 * `config.goal` — the autonomous goal loop. The judge falls back to the agent's
 * own model when `judgeModelConfigId` is absent; the loop is inert until an
 * objective is set.
 */
export const AgentGoalConfigSchema = z
    .object({
    judgeModelConfigId: z.string().min(1).optional(),
    maxRuns: z.number().int().positive().optional(),
    prompt: z.string().max(20_000).optional(),
})
    .strict();
/** Continuity / host-duty mode: never re-arm control ticks (`off`), always, or only while a mission is open. */
export const ContinuityModeSchema = z.enum(['off', 'always', 'mission']);
/**
 * `config.continuity` (alias `config.hostDuty`). Default off. When on, the
 * SessionHost re-arms control ticks so an idle thread keeps tending its mission.
 */
export const AgentContinuityConfigSchema = z
    .object({
    enabled: z.boolean().optional(),
    mode: ContinuityModeSchema.optional(),
    windowMs: z.number().int().positive().optional(),
    /** Whether the continuity tick may run the task list; `false` disables it. */
    tasks: z.boolean().optional(),
})
    .strict();
/**
 * `config.codeMode`. When enabled the agent gains an `execute_typescript` tool
 * running model-authored TS in a LocalSandbox. `toolIds` restricts which tools
 * the sandbox may call; `timeout` caps execution time (ms — matches the host key).
 */
export const AgentCodeModeConfigSchema = z
    .object({
    enabled: z.boolean().optional(),
    toolIds: z.array(z.string().min(1)).optional(),
    timeout: z.number().int().positive().optional(),
})
    .strict();
/**
 * The strict, typed behavior config — the `behaviorConfig` API field. Every
 * sub-object is optional (omit ⇒ leave that behavior unconfigured); unknown
 * top-level keys are rejected so a misspelled `reflction` is a 400, not a
 * silent no-op.
 */
export const AgentBehaviorConfigSchema = z
    .object({
    memory: AgentMemoryConfigSchema.optional(),
    reflection: AgentReflectionConfigSchema.optional(),
    goal: AgentGoalConfigSchema.optional(),
    continuity: AgentContinuityConfigSchema.optional(),
    codeMode: AgentCodeModeConfigSchema.optional(),
    /**
     * `config.anchors` — the agent's static situational scope (DEV-17252,
     * §4.4): the device/robot-class/site this agent supervises. Merged under
     * the waking event's attributes to form the turn's anchor set, which a
     * mid-turn `save_memory` inherits into `subjectRef` deterministically
     * (write-time auto-stamping, zero model calls). Keys outside the anchor
     * vocabulary are rejected here and ignored by the tolerant reader.
     */
    anchors: MemoryAnchorSetSchema.optional(),
})
    .strict();
// ─── Tolerant reader (mirrors the host resolvers, incl. legacy shorthands) ────
const asRecord = (v) => v && typeof v === 'object' && !Array.isArray(v) ? v : undefined;
/** Mirrors `runtime.ts resolveMemory` acceptance (plus the additive `mode`). */
function parseMemory(raw) {
    const m = asRecord(raw);
    if (!m)
        return undefined;
    const out = {};
    if (typeof m.modelConfigId === 'string' && m.modelConfigId.length > 0)
        out.modelConfigId = m.modelConfigId;
    if (typeof m.maxLearnings === 'number')
        out.maxLearnings = m.maxLearnings;
    if (MemoryModeSchema.safeParse(m.mode).success)
        out.mode = m.mode;
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Mirrors `reflection.ts resolveReflectionConfig` acceptance (`true` | object). */
function parseReflection(raw) {
    if (raw === true)
        return { enabled: true };
    const c = asRecord(raw);
    if (!c)
        return undefined;
    const out = { enabled: c.enabled !== false };
    if (typeof c.cron === 'string' && c.cron.trim().length > 0)
        out.cron = c.cron;
    if (typeof c.charter === 'string')
        out.charter = c.charter;
    if (typeof c.prompt === 'string')
        out.prompt = c.prompt;
    if (typeof c.salienceThreshold === 'number')
        out.salienceThreshold = c.salienceThreshold;
    if (typeof c.peerToolErrorThreshold === 'number')
        out.peerToolErrorThreshold = c.peerToolErrorThreshold;
    if (typeof c.judgePrompt === 'string' && c.judgePrompt.trim().length > 0)
        out.judgePrompt = c.judgePrompt;
    if (typeof c.judgeModelConfigId === 'string' && c.judgeModelConfigId.length > 0)
        out.judgeModelConfigId = c.judgeModelConfigId;
    return out;
}
/** Mirrors `runtime.ts resolveGoal` acceptance (object → picked fields). */
function parseGoal(raw) {
    const g = asRecord(raw);
    if (!g)
        return undefined;
    const out = {};
    if (typeof g.judgeModelConfigId === 'string' && g.judgeModelConfigId.length > 0)
        out.judgeModelConfigId = g.judgeModelConfigId;
    if (typeof g.maxRuns === 'number')
        out.maxRuns = g.maxRuns;
    if (typeof g.prompt === 'string')
        out.prompt = g.prompt;
    return out;
}
/** Mirrors `runtime.ts resolveContinuity` acceptance, incl. the `hostDuty` alias + string/`true` forms. */
function parseContinuity(raw) {
    if (raw === true)
        return { enabled: true, mode: 'always' };
    if (raw === 'mission')
        return { enabled: true, mode: 'mission' };
    if (raw === 'always')
        return { enabled: true, mode: 'always' };
    const c = asRecord(raw);
    if (!c)
        return undefined;
    if (c.enabled === false || c.mode === 'off')
        return { enabled: false, mode: 'off' };
    const mode = c.mode === 'mission' || c.when === 'mission' || c.missionOnly === true ? 'mission' : 'always';
    const out = { enabled: true, mode };
    if (typeof c.windowMs === 'number' && c.windowMs > 0)
        out.windowMs = c.windowMs;
    if (c.tasks === false)
        out.tasks = false;
    return out;
}
/** Mirrors `runtime.ts resolveCodeMode` acceptance (`true` | object), honoring an explicit `enabled: false`. */
function parseCodeMode(raw) {
    if (!raw)
        return undefined;
    if (raw === true)
        return { enabled: true };
    const c = asRecord(raw);
    if (!c)
        return undefined;
    // Now that `enabled` is a documented field, an explicit `false` disables —
    // present-but-off, so a round-trip preserves the operator's intent.
    if (c.enabled === false)
        return { enabled: false };
    const out = { enabled: true };
    // Preserve an empty toolIds array (restrict the sandbox to ZERO tools) —
    // dropping it would flip the meaning to "all tools", the opposite intent.
    if (Array.isArray(c.toolIds)) {
        out.toolIds = c.toolIds.filter((t) => typeof t === 'string');
    }
    if (typeof c.timeout === 'number')
        out.timeout = c.timeout;
    return out;
}
/**
 * Tolerant reader: raw `Agent.config` → normalized {@link AgentBehaviorConfig}.
 * Mirrors the persistent-agent host resolvers exactly (including legacy
 * shorthands and the `config.continuity`/`config.hostDuty` alias). Never throws;
 * unrecognized junk is ignored. This is the single place the host should read
 * behavior config from once WS-D commit 3 lands.
 */
export function parseAgentBehaviorConfig(config) {
    const c = config ?? {};
    const memory = parseMemory(c.memory);
    const reflection = parseReflection(c.reflection);
    const goal = parseGoal(c.goal);
    const continuity = parseContinuity(c.continuity ?? c.hostDuty);
    const codeMode = parseCodeMode(c.codeMode);
    // Static anchor scope (DEV-17252): whitelist-extracted, never throws —
    // junk keys/values in a stored blob are dropped, not fatal.
    const anchors = extractMemoryAnchors(asRecord(c.anchors));
    return {
        ...(memory ? { memory } : {}),
        ...(reflection ? { reflection } : {}),
        ...(goal ? { goal } : {}),
        ...(continuity ? { continuity } : {}),
        ...(codeMode ? { codeMode } : {}),
        ...(anchors ? { anchors } : {}),
    };
}
/**
 * Merge a validated `behaviorConfig` into a raw `config` blob for storage. Sub-
 * object keys match the host's `config.*` keys 1:1, so the current host reads
 * the merged result with no change. Every unrelated `config` key (and unknown
 * forward-compat keys) is preserved.
 *
 * Sub-objects are DEEP-merged one level: a partial patch like
 * `{ memory: { mode: 'off' } }` keeps the sibling keys already stored
 * (`modelConfigId`, `maxLearnings`) rather than replacing the whole `memory`
 * object — so a caller can toggle one field without knowing the rest. (One
 * consequence: this door can add/overwrite a sub-key but not delete one; the
 * console clears fields by writing the full `config` blob directly.)
 */
export function mergeBehaviorConfigIntoConfig(config, behaviorConfig) {
    const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
    const merged = { ...(config ?? {}) };
    for (const [key, incoming] of Object.entries(behaviorConfig)) {
        const existing = merged[key];
        merged[key] =
            isPlainObject(incoming) && isPlainObject(existing) ? { ...existing, ...incoming } : incoming;
    }
    return merged;
}
//# sourceMappingURL=agent-behavior-config.js.map